// src/pages/FeaturesPage.jsx
import React from "react";
import { motion } from "framer-motion";

const featureCards = [
  {
    label: "Call Handling",
    title: "Zero Missed Calls. Infinite Capacity.",
    body: "Calyro answers instantly on the first ring, handling multiple callers simultaneously. No busy signals, no voicemail jail.",
    points: [
      "Handles massive call spikes instantly",
      "Answers on the first ring, every time",
      "Multiple concurrent callers supported",
      "Works 24/7, even during holidays",
    ],
    iconSvg: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    accent: "from-sky-500/10 to-sky-400/5",
    iconColor: "text-sky-600",
    pill: "Always-on receptionist",
  },
  {
    label: "Smart Booking",
    title: "Precision Scheduling (Conflict-Free)",
    body: "Every slot is validated against your real-world logic—staff availability, room resources, and buffer times. It never invents times or double-books.",
    points: [
      "Respects \"Prep Time\" and Travel Buffers",
      "Real-time calendar sync",
      "Multi-provider & multi-location aware",
      "Automatic conflict prevention",
    ],
    iconSvg: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    accent: "from-emerald-500/10 to-emerald-400/5",
    iconColor: "text-emerald-600",
    pill: "Zero double-bookings",
  },
  {
    label: "Lead Capture",
    title: "Total Lead Capture & CRM Sync",
    body: "Calyro captures name, intent, and urgency on every call, then pushes that data directly into your software.",
    points: [
      "Flags \"Urgent\" vs \"Routine\" automatically",
      "Captures contact details and call intent",
      "Syncs directly to your CRM",
      "Creates follow-up tasks automatically",
    ],
    iconSvg: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    accent: "from-violet-500/10 to-fuchsia-400/5",
    iconColor: "text-violet-600",
    pill: "No lead left behind",
  },
  {
    label: "Insights",
    title: "Revenue Intelligence",
    body: "Simple, honest analytics that show exactly which calls turned into booked revenue. No vanity metrics.",
    points: [
      "See exactly how much revenue the AI booked this week",
      "Track answered vs missed calls",
      "Identify peak hours and busy days",
      "Export data whenever you want",
    ],
    iconSvg: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    accent: "from-amber-500/10 to-orange-400/5",
    iconColor: "text-amber-600",
    pill: "Honest analytics",
  },
];

const animatedChips = [
  "Answers every caller instantly",
  "Books appointments in real time",
  "Understands intent on every call",
  "Creates follow-up tasks automatically",
  "Never double-books your calendar",
  "Works with multiple locations",
  "Captures every lead cleanly",
  "Gives you clear call & booking stats",
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

export function FeaturesPage() {
  return (
    <main className="min-h-screen bg-slate-50">
      {/* Top hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(79,70,229,0.08),_transparent_55%),_radial-gradient(circle_at_bottom,_rgba(56,189,248,0.08),_transparent_55%)]" />
        <div className="relative mx-auto flex max-w-6xl flex-col gap-10 px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-20">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white/70 px-4 py-1 text-xs font-medium text-slate-600 shadow-xs backdrop-blur"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Built for businesses that live on the phone
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl space-y-4 sm:space-y-5"
          >
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl md:text-5xl leading-tight">
              The 24/7 Front Desk{" "}
              <span className="bg-gradient-to-r from-sky-500 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                That Runs Itself.
              </span>
            </h1>
            <p className="text-sm text-slate-600 sm:text-base lg:text-lg leading-relaxed">
              Calyro combines the empathy of a human receptionist with the speed of software. It answers, books, qualifies, and syncs—so you can stop juggling phones and start growing your business.
            </p>
          </motion.div>

          {/* Quick value chips */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid gap-3 sm:gap-4 text-sm text-slate-700 grid-cols-1 sm:grid-cols-3"
          >
            <motion.div 
              variants={fadeInUp}
              className="group relative flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-lg hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 backdrop-blur"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="relative z-10">
                <div className="font-bold text-slate-900 text-sm sm:text-base">Live in 24–48 Hours</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Custom-built with industry playbooks and your business rules.
                </div>
              </div>
            </motion.div>
            <motion.div 
              variants={fadeInUp}
              className="group relative flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-lg hover:shadow-xl hover:border-violet-300 hover:-translate-y-1 transition-all duration-300 backdrop-blur"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center group-hover:bg-violet-500/20 transition-colors">
                <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="relative z-10">
                <div className="font-bold text-slate-900 text-sm sm:text-base">Your Rules, Your Way</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Every decision follows your custom hours, pricing, and scripts.
                </div>
              </div>
            </motion.div>
            <motion.div 
              variants={fadeInUp}
              className="group relative flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-lg hover:shadow-xl hover:border-emerald-300 hover:-translate-y-1 transition-all duration-300 backdrop-blur"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="relative z-10">
                <div className="font-bold text-slate-900 text-sm sm:text-base">Built for Volume</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Handles simultaneous calls for Clinics, Trades, and Agencies.
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="relative border-y border-slate-100 bg-white/70">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-slate-100/70 to-transparent" />
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="relative mx-auto grid max-w-6xl gap-6 px-4 py-12 sm:px-6 md:grid-cols-2 lg:px-8 lg:py-16"
        >
          {featureCards.map((card, index) => (
            <motion.article
              key={card.title}
              variants={scaleIn}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="group relative flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-8 shadow-xl hover:shadow-2xl hover:border-blue-300 transition-all duration-300"
            >
              {/* Gradient glow on hover */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${card.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              <div className="absolute inset-0 rounded-3xl shadow-inner opacity-0 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative z-10 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm">
                  <span className={`${card.iconColor}`}>{card.iconSvg}</span>
                  <span className="text-xs sm:text-xs">{card.label}</span>
                </span>
                <span
                  className={`inline-flex items-center rounded-full bg-gradient-to-r ${card.accent} border border-slate-200 px-3 py-1 text-[10px] sm:text-[11px] font-semibold text-slate-700 shadow-md whitespace-nowrap`}
                >
                  {card.pill}
                </span>
              </div>

              <div className="relative z-10 space-y-2 sm:space-y-3">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight">
                  {card.title}
                </h2>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{card.body}</p>
              </div>

              <ul className="relative z-10 mt-3 sm:mt-4 space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-slate-700">
                {card.points.map((point) => (
                  <li key={point} className="flex items-start gap-2 sm:gap-3 group/item">
                    <div className="mt-[2px] w-5 h-5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500/20 transition-colors">
                      <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-medium leading-snug">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* Animated capability strip */}
      <section className="border-b border-slate-100 bg-slate-50/60">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 sm:gap-6 px-4 py-8 sm:py-10 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-between gap-4"
          >
            <div className="space-y-1 sm:space-y-0">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 leading-tight">
                An agent that actually does the work.
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-snug">
                Calyro quietly handles all of this while your team focuses on real work.
              </p>
            </div>
          </motion.div>

          {/* Grid Layout for All Screens */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
          >
            {animatedChips.map((chip, index) => (
              <motion.div
                key={chip}
                variants={fadeInUp}
                whileHover={{ scale: 1.02, y: -2 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-2 sm:gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:px-5 sm:py-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-md hover:shadow-lg hover:border-blue-300 transition-all duration-300"
              >
                <span className="h-2 w-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-500 flex-shrink-0" />
                <span className="leading-snug">{chip}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* White-Glove Setup Section */}
      <section id="white-glove-setup" className="scroll-mt-24 border-b border-slate-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:py-18 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-violet-500/10 border border-violet-500/20 px-4 py-1.5 text-xs font-semibold text-violet-600 uppercase tracking-wider mb-4">
              <span className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
              White-Glove Onboarding
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Why We Take 24–48 Hours{" "}
              <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                (And Why It Matters)
              </span>
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              We don't do instant setups because rushed AI agents sound robotic, miss details, and cost you revenue. 
              Here's exactly what happens before your agent goes live.
            </p>
          </motion.div>

          {/* 3-Step Process */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6 mb-10"
          >
            {[
              {
                step: "1",
                title: "Custom Configuration",
                desc: "Your specific services, hours, providers, and business rules are programmed into your agent.",
                details: ["Call flow & script design", "Greetings & qualification", "FAQ responses", "Escalation rules"],
                color: "blue"
              },
              {
                step: "2",
                title: "Voice & Tone Tuning",
                desc: "Pacing, interruption handling, and pronunciation are calibrated to sound natural and professional.",
                details: ["Natural pacing", "Interruption behavior", "Tone adjustments", "Pronunciations"],
                color: "violet"
              },
              {
                step: "3",
                title: "Test Calls & QA",
                desc: "We run real test scenarios to ensure your agent handles edge cases before a single customer hears it.",
                details: ["Real scenario testing", "Edge case handling", "Quality assurance", "Final approval"],
                color: "emerald"
              }
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                variants={fadeInUp}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-lg hover:shadow-2xl hover:border-blue-300 transition-all"
              >
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r ${
                  item.color === 'blue' ? 'from-blue-500 to-sky-500' :
                  item.color === 'violet' ? 'from-violet-500 to-purple-500' :
                  'from-emerald-500 to-teal-500'
                }`} />
                
                <div className={`w-10 h-10 rounded-xl mb-4 flex items-center justify-center text-white font-bold text-lg ${
                  item.color === 'blue' ? 'bg-gradient-to-br from-blue-500 to-sky-500' :
                  item.color === 'violet' ? 'bg-gradient-to-br from-violet-500 to-purple-500' :
                  'bg-gradient-to-br from-emerald-500 to-teal-500'
                }`}>
                  {item.step}
                </div>
                
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">{item.desc}</p>
                
                <ul className="space-y-2">
                  {item.details.map((detail, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                      <svg className={`w-4 h-4 flex-shrink-0 ${
                        item.color === 'blue' ? 'text-blue-500' :
                        item.color === 'violet' ? 'text-violet-500' :
                        'text-emerald-500'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* What's Included List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8 shadow-lg"
          >
            <h3 className="text-lg font-bold text-slate-900 mb-4">Everything Included in Your Setup</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "Call flow + script design", desc: "Greetings, qualification, FAQs, escalation rules" },
                { title: "Booking logic", desc: "Services, hours, buffers, multi-staff, multi-location rules" },
                { title: "Voice tuning", desc: "Pacing, interruption behavior, tone, pronunciations" },
                { title: "Integrations", desc: "Calendar/CRM/webhooks configured (as applicable)" },
                { title: "QA testing", desc: "Test calls before go-live — we don't ship broken calls" },
                { title: "Dashboard setup", desc: "Your portal configured with all call logs & analytics" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all">
                  <div className="w-6 h-6 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Outcome Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 p-6 text-center"
          >
            <p className="text-base sm:text-lg font-semibold text-emerald-800">
              <strong>The Result:</strong> You go live with an agent that sounds human, follows your business rules, 
              and produces clean call summaries + lead/booking data in your dashboard.
            </p>
            <p className="text-sm text-emerald-700 mt-2 font-medium">
              We don't go live until it sounds like your best employee.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Revenue section – honest & clicky */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-6xl gap-8 sm:gap-10 px-4 py-12 sm:py-14 sm:px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:px-8 lg:py-18">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="space-y-4 sm:space-y-5"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              Revenue impact, no made-up numbers
            </motion.div>

            <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl lg:text-3xl leading-tight">
              Plug the Holes in Your{" "}
              <span className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">
                Revenue Bucket.
              </span>
            </h2>

            <p className="text-sm text-slate-600 sm:text-base leading-relaxed">
              Calyro patches every weak point in your call flow. It captures the 62% of leads that call after-hours, converts inquiries instantly, and creates a safety net for every opportunity.
            </p>

            <ul className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="mt-[2px] text-emerald-500 text-base">✔</span>
                <span className="font-medium leading-snug">Eliminates missed-call revenue loss</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[2px] text-emerald-500 text-base">✔</span>
                <span className="font-medium leading-snug">Converts callers while their intent is high</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[2px] text-emerald-500 text-base">✔</span>
                <span className="font-medium leading-snug">Detects urgent buying signals in real-time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[2px] text-emerald-500 text-base">✔</span>
                <span className="font-medium leading-snug">Auto-tasks your team for complex follow-ups</span>
              </li>
            </ul>

            <div className="pt-2">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
              >
                See Your Potential Revenue
                <span aria-hidden="true">→</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Right side: simple "live" summary card */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative group"
          >
            <div className="absolute -inset-x-6 -inset-y-6 rounded-3xl bg-gradient-to-br from-sky-100/70 via-emerald-50/60 to-fuchsia-100/60 blur-3xl group-hover:blur-2xl transition-all duration-500" />
            <motion.div 
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-3xl border-2 border-slate-200 bg-white p-8 shadow-2xl hover:shadow-3xl hover:border-blue-300 transition-all duration-300 backdrop-blur"
            >
              <header className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Live impact snapshot
                  </div>
                  <div className="text-[11px] text-slate-500">
                    All numbers pulled from your real calls.
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Live
                </span>
              </header>

              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 px-5 py-4 shadow-md hover:shadow-lg transition-shadow">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Today
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      Calls answered, missed, and booked
                    </div>
                    <div className="text-xs text-slate-500">
                      Updated in real time from Calyro.
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>Calls: <span className="font-semibold text-slate-900">—</span></div>
                    <div>Bookings: <span className="font-semibold text-slate-900">—</span></div>
                    <div>Follow-ups: <span className="font-semibold text-slate-900">—</span></div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="group/stat rounded-2xl bg-gradient-to-br from-sky-50 to-sky-100/50 border border-sky-200 px-4 py-4 text-xs text-slate-700 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-sky-700">
                      Missed Calls Caught
                    </div>
                    <p className="font-medium">See how many "would have been lost" calls Calyro rescued.</p>
                  </div>
                  <div className="group/stat rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 px-4 py-4 text-xs text-slate-700 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                      Bookings From Calls
                    </div>
                    <p className="font-medium">Track how many conversations turn into appointments.</p>
                  </div>
                  <div className="group/stat rounded-2xl bg-gradient-to-br from-violet-50 to-violet-100/50 border border-violet-200 px-4 py-4 text-xs text-slate-700 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-violet-700">
                      Follow-ups Created
                    </div>
                    <p className="font-medium">Know exactly what your team needs to act on next.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
